import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, Award, DollarSign, Gift,ExternalLink, Zap, ChevronRight, Loader, AlertCircle, User, Users, Cpu, ArrowRight } from 'lucide-react';
import marketingService from '../services/marketingService';
import UpgradeAnalysisModal from './UpgradeAnalysisModal';
import Nt from './Nt';

const ChainAnalysis = () => {
    const [availableTariffs, setAvailableTariffs] = useState([]);

    // Основные параметры поиска
    const [selectedTarif, setSelectedTarif] = useState('');
    const [selectedLevel, setSelectedLevel] = useState(0);

    // Параметры фильтрации
    const [depth, setDepth] = useState(1);
    const [closedFilter, setClosedFilter] = useState(null);

    const [login, setLogin] = useState('');
    const [loading, setLoading] = useState(true);
    const [analyzing, setAnalyzing] = useState(false);
    const [analysisData, setAnalysisData] = useState(null);
    const [error, setError] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedUpgrades, setSelectedUpgrades] = useState(null);

    useEffect(() => {
        loadAvailableOptions();
    }, []);

    const loadAvailableOptions = async () => {
        setLoading(true);
        try {
            const tariffs = [
                {
                    name: 'start',
                    display_name: 'Start',
                    levels: [
                        { level: 5, number_level: 1 },
                        { level: 8, number_level: 2 },
                        { level: 16, number_level: 3 },
                        { level: 24, number_level: 4 }
                    ]
                },
                {
                    name: 'business',
                    display_name: 'Business',
                    levels: [
                        { level: 40, number_level: 1 },
                        { level: 80, number_level: 2 },
                        { level: 160, number_level: 3 },
                        { level: 240, number_level: 4 }
                    ]
                },
                {
                    name: 'profit',
                    display_name: 'Profit',
                    levels: [
                        { level: 400, number_level: 1 },
                        { level: 800, number_level: 2 },
                        { level: 1600, number_level: 3 },
                        { level: 2400, number_level: 4 }
                    ]
                },
                {
                    name: 'turbo',
                    display_name: 'Turbo',
                    levels: [
                        { level: 4000, number_level: 1 },
                        { level: 8000, number_level: 2 },
                        { level: 16000, number_level: 3 },
                        { level: 24000, number_level: 4 }
                    ]
                },
                {
                    name: 'vip',
                    display_name: 'VIP',
                    levels: [
                        { level: 40000, number_level: 1 },
                        { level: 80000, number_level: 2 },
                        { level: 160000, number_level: 3 },
                        { level: 240000, number_level: 4 }
                    ]
                },
                {
                    name: 'rivers',
                    display_name: 'RIVERS',
                    levels: [
                        { level: 45, number_level: 1 },
                        { level: 90, number_level: 2 },
                        { level: 180, number_level: 3 },
                        { level: 275, number_level: 4 }
                    ]
                }
            ];

            setAvailableTariffs(tariffs);

            if (tariffs.length > 0) {
                setSelectedTarif(tariffs[0].name);
                setSelectedLevel(tariffs[0].levels[0].level);
            }
        } catch (error) {
            console.error('Error loading options:', error);
            setError('Ошибка при загрузке опций');
        } finally {
            setLoading(false);
        }
    };

    const handleAnalyze = async () => {
        if (!login.trim()) {
            setError('Введите логин для анализа');
            return;
        }

        if (!selectedTarif || !selectedLevel) {
            setError('Выберите тариф и уровень для анализа');
            return;
        }

        setAnalyzing(true);
        setError(null);
        setAnalysisData(null);

        try {
            const params = {
                login: login.trim(),
                tarif: selectedTarif,
                level: selectedLevel,
                depth: depth,
                closed_filter: closedFilter,
            };

            const response = await marketingService.analyzeChain(params);

            if (response.success) {
                setAnalysisData(response.data);
            } else {
                setError(response.message || 'Ошибка при анализе цепочек');
            }
        } catch (err) {
            console.error('Analysis error:', err);
            setError('Ошибка соединения с сервером');
        } finally {
            setAnalyzing(false);
        }
    };

    // ✅ НОВОЕ: Функция для построения полной цепочки апгрейдов
    const buildUpgradeChainString = (closure) => {
        if (!closure || !closure.upgrade) {
            return null;
        }

        const chain = [];
        let currentClosure = closure;

        // Рекурсивно собираем всю цепочку
        while (currentClosure && currentClosure.upgrade) {
            chain.push({
                tarif: currentClosure.upgrade.target_tarif,
                level: currentClosure.upgrade.target_level
            });

            currentClosure = currentClosure.upgrade.next_position?.next_closure;
        }

        return chain;
    };

    const collectUpgradeChain = (closure) => {
        const chain = [];

        const traverse = (currentClosure) => {
            if (!currentClosure || !currentClosure.upgrade) {
                return;
            }

            chain.push({
                ...currentClosure.upgrade,
            });

            if (currentClosure.upgrade.next_position &&
                currentClosure.upgrade.next_position.next_closure) {
                traverse(currentClosure.upgrade.next_position.next_closure);
            }
        };

        traverse(closure);
        return chain;
    };

    const handleShowUpgrades = (closure) => {
        if (!closure || !closure.upgrade) {
            return;
        }

        const upgradeChain = collectUpgradeChain(closure);

        if (upgradeChain.length > 0) {
            setSelectedUpgrades(upgradeChain);
            setModalOpen(true);
        }
    };

    const getCloneTypeIcon = (type) => {
        switch (type) {
            case 'technical':
                return <Cpu size={24} className="text-purple-500" />;
            case 'personal':
                return <Users size={24} className="text-green-500" />;
            default:
                return <User size={24} className="text-blue-500" />;
        }
    };

    const getCloneTypeName = (type) => {
        switch (type) {
            case 'technical':
                return 'Технический клон';
            case 'personal':
                return 'Личный клон';
            default:
                return 'Аккаунт';
        }
    };

    const getCloneTypeColor = (type) => {
        switch (type) {
            case 'technical':
                return 'from-purple-500 to-purple-600';
            case 'personal':
                return 'from-green-500 to-green-600';
            default:
                return 'from-blue-500 to-blue-600';
        }
    };

    const renderNextClosure = (closure) => {
        if (!closure) {
            return (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                    <AlertCircle size={24} className="text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 text-sm">
                        Все позиции закрыты
                    </p>
                </div>
            );
        }

        return (
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border-2 border-blue-300">
                <div className="flex items-center justify-between mb-3">
                    <div className="font-bold text-blue-800 flex items-center gap-2">
                        <div
                            className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                            {closure.position_number}
                        </div>
                        Позиция {closure.position_number}
                    </div>
                </div>

                {/* Прямые вознаграждения */}
                {closure.rewards && closure.rewards.length > 0 && (
                    <div className="mb-3">
                        <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <DollarSign size={16} className="text-green-500"/>
                            Прямые вознаграждения:
                        </div>
                        <div className="space-y-1">
                            {closure.rewards.map((reward, idx) => (
                                <div key={idx}
                                     className="flex justify-between items-center bg-white rounded px-3 py-2 text-sm">
                                    <span className="text-gray-700">
                                        {reward.type === 'awards' ? '💰 Награды' : `📊 Линия ${reward.line}`}
                                        {' → '}{reward.recipient_login}
                                    </span>
                                    <span className="font-bold text-green-600">
                                        +{reward.amount}$
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Реферальные вознаграждения */}
                {closure.referral_rewards && closure.referral_rewards.length > 0 && (
                    <div className="mb-3">
                        <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <Users size={16} className="text-blue-500"/>
                            Реферальные:
                        </div>
                        <div className="space-y-1">
                            {closure.referral_rewards.map((reward, idx) => (
                                <div key={idx}
                                     className="flex justify-between items-center bg-white rounded px-3 py-2 text-sm">
                                    <span className="text-gray-700">
                                        🔗 Уровень {reward.level} → {reward.recipient_login}
                                    </span>
                                    <span className="font-bold text-blue-600">
                                        +{reward.amount}$
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Клоны */}
                {closure.clones && closure.clones.length > 0 && (
                    <div className="mb-3">
                        <div className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                            <Gift size={16} className="text-purple-500"/>
                            Начисление клонов:
                        </div>
                        <div className="space-y-1">
                            {closure.clones.map((clone, idx) => (
                                <div key={idx}
                                     className="flex justify-between items-center bg-white rounded px-3 py-2 text-sm">
                                    <span className="text-gray-700">
                                        {clone.type === 'technical' ? '🤖 Технические' : '👥 Обычные'}
                                    </span>
                                    <span className="font-bold text-purple-600">
                                        +{clone.count}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Общая сумма */}
                <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                        <span className="font-bold text-gray-800">Всего вознаграждений:</span>
                        <span className="text-2xl font-bold text-green-600">
                            {closure.total_rewards}$
                        </span>
                    </div>
                </div>

                {/* Кнопка апгрейда */}
                {closure.upgrade && (
                    <div className="mt-4 pt-4 border-t">
                        <button
                            onClick={() => handleShowUpgrades(closure)}
                            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-3 rounded-lg font-semibold hover:from-orange-600 hover:to-orange-700 transition flex items-center justify-center gap-2"
                        >
                            <Zap size={20}/>
                            Посмотреть цепочку апгрейдов
                            <ChevronRight size={20}/>
                        </button>
                    </div>
                )}

            </div>
        );
    };

    const renderPositionCard = (position, index) => {
        // ✅ НОВОЕ: Строим цепочку апгрейдов для отображения
        const upgradeChain = buildUpgradeChainString(position.next_closure);

        return (
            <div
                key={index}
                className={`bg-gradient-to-r ${getCloneTypeColor(position.clone_type)} rounded-2xl shadow-lg overflow-hidden`}
            >
                <div className="bg-white rounded-t-2xl">
                    <div className="p-6 flex justify-between items-start">
                        <div className="flex items-center gap-3">
                            {getCloneTypeIcon(position.clone_type)}
                            <div>
                                <div className="font-bold text-lg">
                                    Позиция #{position.position_id}
                                </div>
                                <a
                                    href={`https://light-platform.com/dashboard/clusters?program=${position.tarif}&tarif=${position.tarif}&level=${position.level}&mentor=${position.position_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 w-auto"
                                    title="Перейти к позиции апгрейда"
                                    style={{ width: '50px' }}>
                                    <ExternalLink size={20}/>
                                </a>
                                <div className="text-sm opacity-90">
                                    <Nt>{position.login}</Nt> ({getCloneTypeName(position.clone_type)})
                                </div>

                                {/* ✅ НОВОЕ: Отображение полной цепочки апгрейдов */}
                                <div className="text-sm text-gray-600 mt-1 flex items-center gap-1 flex-wrap">
                                    <span className="font-semibold">{position.tarif?.toUpperCase()}</span>
                                    <span>-</span>
                                    <span>{position.level}$</span>

                                    {upgradeChain && upgradeChain.length > 0 && (
                                        <>
                                            {upgradeChain.map((upgrade, idx) => (
                                                <React.Fragment key={idx}>
                                                    <ArrowRight size={14} className="text-orange-500"/>
                                                    <span className="font-semibold text-orange-600">
                                                        {upgrade.tarif?.toUpperCase()}
                                                    </span>
                                                    <span className="text-orange-600">-</span>
                                                    <span className="text-orange-600">{upgrade.level}$</span>
                                                </React.Fragment>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-2xl font-bold">
                                {position.closed}/{position.total_positions}
                            </div>
                            <div className="text-sm opacity-90">
                                {position.created_at}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Анализ следующего закрытия */}
                <div className="p-4">
                    <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <TrendingUp size={20} className="text-blue-500" />
                        Следующее закрытие:
                    </h4>
                    {renderNextClosure(position.next_closure)}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-[#d5ebe6] to-white flex items-center justify-center">
                <div className="text-center">
                    <Loader className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" />
                    <p className="text-gray-600">Загрузка...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#d5ebe6] to-white py-8 px-4">
            <div className="max-w-6xl mx-auto">
                {/* Заголовок */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <h1 className="text-3xl font-bold text-gray-800 mb-2 flex items-center gap-3">
                        <TrendingUp size={32} className="text-blue-500" />
                        Анализ цепочек
                    </h1>
                    <p className="text-gray-600">
                        Визуализация последствий закрытия позиций с учетом глубины поиска и апгрейдов
                    </p>
                </div>

                {/* Форма поиска */}
                <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                        <Search size={20} />
                        Параметры поиска
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {/* Логин */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Логин пользователя
                            </label>
                            <input
                                type="text"
                                value={login}
                                onChange={(e) => setLogin(e.target.value)}
                                placeholder="Введите логин"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                onKeyPress={(e) => e.key === 'Enter' && handleAnalyze()}
                            />
                        </div>

                        {/* Тариф */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Тариф
                            </label>
                            <select
                                value={selectedTarif}
                                onChange={(e) => {
                                    setSelectedTarif(e.target.value);
                                    const tariff = availableTariffs.find(t => t.name === e.target.value);
                                    if (tariff) setSelectedLevel(tariff.levels[0].level);
                                }}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {availableTariffs.map(tariff => (
                                    <option key={tariff.name} value={tariff.name}>
                                        {tariff.display_name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Уровень */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Уровень
                            </label>
                            <select
                                value={selectedLevel}
                                onChange={(e) => setSelectedLevel(parseInt(e.target.value))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                {availableTariffs
                                    .find(t => t.name === selectedTarif)
                                    ?.levels.map(level => (
                                        <option key={level.level} value={level.level}>
                                            Уровень {level.number_level} - {level.level}$
                                        </option>
                                    ))}
                            </select>
                        </div>

                        {/* Глубина поиска */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Глубина поиска (1-10)
                            </label>
                            <input
                                type="number"
                                min="1"
                                max="10"
                                value={depth}
                                onChange={(e) => setDepth(parseInt(e.target.value) || 1)}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <p className="text-xs text-gray-500 mt-1">
                                Глубина поиска личников
                            </p>
                        </div>
                    </div>

                    {/* Вторая строка - фильтр */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Фильтр по закрытым местам
                            </label>
                            <select
                                value={closedFilter === null ? '' : closedFilter}
                                onChange={(e) => setClosedFilter(e.target.value === '' ? null : parseInt(e.target.value))}
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="">Все незакрытые</option>
                                <option value="0">Только 0 закрытий</option>
                                <option value="1">Только 1 закрытие</option>
                                <option value="2">Только 2 закрытия</option>
                                <option value="3">Только 3 закрытия</option>
                            </select>
                        </div>
                    </div>

                    {/* Кнопка анализа */}
                    <button
                        onClick={handleAnalyze}
                        disabled={analyzing || !login.trim()}
                        className="w-full mt-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {analyzing ? (
                            <>
                                <Loader className="animate-spin" size={20} />
                                Анализ...
                            </>
                        ) : (
                            <>
                                <Search size={20} />
                                Анализировать
                            </>
                        )}
                    </button>
                </div>

                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                        <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle size={20} />
                            <span className="font-medium">{error}</span>
                        </div>
                    </div>
                )}

                {analysisData && (
                    <div className="space-y-6">
                        <div className="bg-white rounded-xl shadow-lg p-6">
                            <h2 className="text-2xl font-bold text-gray-800 mb-2">
                                Результаты анализа
                            </h2>
                            <p className="text-gray-600">
                                Найдено позиций: <span className="font-bold text-blue-600">{analysisData.positions?.length || 0}</span>
                            </p>
                            {analysisData.user_info && (
                                <div className="mt-4 bg-blue-50 rounded-lg p-4">
                                    <div className="font-medium text-gray-800">
                                        Пользователь: <Nt>{analysisData.user_info.login}</Nt>
                                    </div>
                                    <div className="text-sm text-gray-600">
                                        ID: {analysisData.user_info.id}
                                    </div>
                                </div>
                            )}

                            {/* Параметры поиска */}
                            {analysisData.search_params && (
                                <div className="mt-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                                    <h3 className="font-semibold text-gray-800 mb-2">Параметры поиска:</h3>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                                        <div>
                                            <span className="text-gray-600">Тариф:</span>
                                            <span className="ml-2 font-bold text-blue-600">
                                                {analysisData.search_params.tarif?.toUpperCase()}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Уровень:</span>
                                            <span className="ml-2 font-bold text-blue-600">
                                                {analysisData.search_params.level}$
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Глубина:</span>
                                            <span className="ml-2 font-bold text-blue-600">
                                                {analysisData.search_params.depth}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-600">Фильтр:</span>
                                            <span className="ml-2 font-bold text-blue-600">
                                                {analysisData.search_params.closed_filter === null ? 'Все' : `${analysisData.search_params.closed_filter} закрытий`}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {analysisData.positions && analysisData.positions.length > 0 ? (
                            analysisData.positions.map((position, index) =>
                                renderPositionCard(position, index)
                            )
                        ) : (
                            <div className="bg-white rounded-xl shadow-lg p-8 text-center">
                                <AlertCircle size={48} className="text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-600 text-lg">
                                    Не найдено позиций для анализа
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Модальное окно с цепочкой апгрейдов */}
            <UpgradeAnalysisModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                upgrades={selectedUpgrades}
            />
        </div>
    );
};

export default ChainAnalysis;
